import { REDACTION_MARK } from '@zuruflex/types';
/**
 * What a thread's status may become, what a message does to it, and what
 * may be said in one before a booking is paid for.
 *
 * Here rather than in the API because four surfaces have to agree: the
 * admin inbox sets a status by hand, the partner portal and the website
 * change one by replying, and the sweep changes one by the clock. A rule
 * implemented four times is a rule with four behaviours.
 *
 * Nothing in this file does I/O or knows what Prisma is.
 */
/** How long a RESOLVED thread waits for a reply before it closes for good. */
export const AUTO_CLOSE_DAYS = 7;
/**
 * Where an agent may move a thread from where it is.
 *
 * `CLOSED` is not reachable by hand. Closing is what the sweep does to a
 * resolved thread nobody came back to, and an agent who wants it gone
 * wants `RESOLVED` — the difference being that a resolved thread can still
 * be reopened by its requester, which is usually the thing they need.
 */
const TRANSITIONS = {
    OPEN: ['WAITING', 'SNOOZED', 'RESOLVED'],
    WAITING: ['OPEN', 'SNOOZED', 'RESOLVED'],
    SNOOZED: ['OPEN', 'WAITING', 'RESOLVED'],
    RESOLVED: ['OPEN'],
    // Terminal: a later message opens a new thread rather than reviving this.
    CLOSED: [],
};
export function allowedThreadTransitions(from) {
    return TRANSITIONS[from];
}
export function canTransitionThread(from, to) {
    return from === to || TRANSITIONS[from].includes(to);
}
/**
 * What sending a message does to the status, or null to leave it alone.
 *
 * The status follows the conversation rather than being administered
 * alongside it. An agent who replies has, by replying, put the ball in the
 * requester's court, and asking them to also set a dropdown means the
 * dropdown is wrong by the end of the first busy morning.
 *
 * A note changes nothing: it is addressed to a colleague, and a thread
 * that flipped to WAITING because somebody wrote "chasing the partner"
 * would be claiming the customer had been answered when they had not.
 */
export function statusAfterMessage(current, author, isPublic) {
    if (current === 'CLOSED')
        return null;
    if (!isPublic)
        return null;
    if (author === 'SYSTEM')
        return null;
    if (author === 'STAFF')
        return current === 'WAITING' ? null : 'WAITING';
    // The requester. A snoozed thread wakes up: they have said something
    // new, and the reason it was put down no longer holds.
    return current === 'OPEN' ? null : 'OPEN';
}
/** A thread this caller has caught up on has no unread count to show. */
export function unreadCount(messageSeq, lastReadSeq) {
    return Math.max(0, messageSeq - lastReadSeq);
}
/**
 * What a composer actually sends.
 *
 * `trim()` deals with the ends and nothing else, which leaves the mess in
 * the middle: a box that grows to five lines collects stray returns the way
 * every text box does, and a message stored with four blank lines in it
 * carries them into the bubble, the inbox preview and the email about the
 * thread.
 *
 * One blank line survives, because a blank line between two paragraphs is
 * somebody writing rather than somebody leaning on return. Beyond that they
 * collapse, and trailing spaces go from the end of every line — they are
 * invisible where they are typed and visible in a bubble that wraps.
 */
export function normaliseMessage(body) {
    return body
        .replace(/\r\n?/g, '\n')
        .replace(/[^\S\n]+$/gm, '')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
}
/**
 * The first line of a message, for an inbox row.
 *
 * Trimmed to a length rather than to a word boundary: the rows are
 * fixed-width and the CSS ellipsis does the honest truncation. This exists
 * to stop a four-thousand-character paste travelling to the browser once
 * per row.
 */
export function messagePreview(body, limit = 140) {
    const firstLine = body.split('\n').find((line) => line.trim().length > 0) ?? '';
    const trimmed = firstLine.trim();
    return trimmed.length <= limit ? trimmed : `${trimmed.slice(0, limit - 1)}…`;
}
/**
 * What an inbox row says for a message that is only files.
 *
 * A photograph of a dented bumper with no caption is a perfectly ordinary
 * message — the API has always allowed one — and until now every inbox drew
 * it as a blank line, because the preview is the first line of a body and
 * there is no body.
 *
 * A key and a count rather than a sentence: the row is read in the reader's
 * own language and this package has none. It is the rules, not the words.
 *
 * A pile that is all pictures says "photos". One PDF among them and it says
 * "files", because "3 photos" for two photographs and a receipt is a row
 * that lies about what is in the thread.
 */
export function attachmentPreviewKey(files) {
    if (files.length === 0)
        return null;
    const allPictures = files.every((file) => file.contentType.startsWith('image/'));
    const one = files.length === 1;
    if (allPictures)
        return { key: one ? 'previewPhoto' : 'previewPhotos', count: files.length };
    return { key: one ? 'previewFile' : 'previewFiles', count: files.length };
}
// ── Contact details ──────────────────────────────────────────
/**
 * Whether a renter and an owner may swap contact details yet.
 *
 * Not "is the car held": `PENDING` holds it, and at `PENDING` nothing has
 * been paid and the owner has not agreed — which is precisely the window
 * in which a booking gets taken off the platform and Zuruflex earns nothing
 * for having made the introduction.
 *
 * From `CONFIRMED` the numbers pass through, because a handover genuinely
 * needs a phone number and pretending otherwise just moves the
 * conversation to a channel with no transcript.
 */
export function contactDetailsAllowed(status) {
    if (status === null)
        return false;
    return status === 'CONFIRMED' || status === 'ACTIVE' || status === 'COMPLETED';
}
/*
 * Three patterns, and the hard part of each is what it must *not* match.
 *
 * `EMAIL` and `URL` are the easy two. `PHONE` is the one that earns its
 * tests: a bare run of digits is also a price, a date, a plate, a booking
 * reference and a time range, and a masker that eats those makes the
 * product look broken to the honest majority in order to inconvenience
 * the few.
 *
 * So it requires seven or more digits — longer than any year, price or
 * date in this product — and it refuses a run that is glued to a letter,
 * which is what keeps `ZF-8K2QD` and `GR-4821-23` intact.
 */
const EMAIL = /\b[\w.+-]+@[\w-]+\.[\w.-]{2,}\b/gi;
const URL = /\b(?:https?:\/\/|www\.)\S+/gi;
const PHONE = /(?<![\w@])\+?\(?\d[\d\s().-]{5,}\d(?![\w@])/g;
/**
 * A date, which is a run of digits and separators like any other.
 *
 * `2026-09-25` is eight digits and would otherwise be masked, which would
 * eat the single most common thing anybody says in one of these threads:
 * when they want the car. A real number written `024-41-2345` also fits
 * this shape and slips through — the rarer formatting is the better thing
 * to get wrong.
 */
const DATE_LIKE = /^\d{1,4}[-/.]\d{1,2}[-/.]\d{2,4}$/;
/**
 * Takes contact details out of a message before it is stored.
 *
 * Masked at write, and only the masked body is kept: a transcript that
 * differs from what was delivered is worse than useless in the dispute it
 * exists for. The sender is shown what actually went, rather than being
 * left to believe their number arrived.
 */
export function maskContactDetails(body) {
    let redacted = false;
    const mark = () => {
        redacted = true;
        return REDACTION_MARK;
    };
    // Addresses and links first: an address contains dots and an address
    // with digits in it would otherwise be half-eaten by the phone pattern,
    // leaving a fragment that still reads as a way to get in touch.
    const masked = body
        .replace(EMAIL, mark)
        .replace(URL, mark)
        .replace(PHONE, (match) => countDigits(match) >= 7 && !DATE_LIKE.test(match.trim()) ? mark() : match);
    return { body: masked, redacted };
}
function countDigits(value) {
    let count = 0;
    for (const character of value) {
        if (character >= '0' && character <= '9')
            count += 1;
    }
    return count;
}
